// AuditLogSection — collapsible per spec §7. Default collapsed; default
// 90-day window (server-default — we omit `from`/`to` so the backend
// applies them). Filters per spec are deferred (see README).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { agreementsApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import {
  ErrorBanner,
  Skeleton,
  EmptyState,
} from './primitives';
import { formatDatetimeET } from '@/utils/format';
import { ApiError } from '@/types/errors';

interface Props {
  agreementId: string;
}

export function AuditLogSection({ agreementId }: Props) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);

  const q = useQuery({
    queryKey: qk.agreementAuditEvents(agreementId, { cursor }),
    queryFn: () =>
      agreementsApi.auditEvents(agreementId, {
        cursor: cursor ?? undefined,
      }),
    enabled: open,
  });

  return (
    <section className="section" aria-labelledby="audit-h2">
      <div className="section-header">
        <h2 id="audit-h2">Audit log</h2>
        <button
          type="button"
          className="btn btn-secondary"
          aria-expanded={open ? 'true' : 'false'}
          aria-controls="audit-content"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Hide' : 'Show'}
        </button>
      </div>
      {open ? (
        <div id="audit-content">
          {q.isPending ? (
            <Skeleton rows={5} height="20px" />
          ) : q.isError ? (
            <ErrorBanner
              message="Couldn't load audit events."
              requestId={q.error instanceof ApiError ? q.error.requestId : undefined}
              onRetry={() => void q.refetch()}
            />
          ) : (q.data?.data.length ?? 0) === 0 ? (
            <EmptyState message="No audit events for this agreement in the last 90 days." />
          ) : (
            <>
              <table className="data-table">
                <caption className="visually-hidden">Audit events</caption>
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Event</th>
                    <th scope="col">Entity</th>
                    <th scope="col">Actor</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data!.data.map((e) => (
                    <tr key={e.id}>
                      <td>{formatDatetimeET(e.occurred_at)}</td>
                      <td>{e.event_name}</td>
                      <td>
                        {e.entity_kind} <span className="muted">{e.entity_id}</span>
                      </td>
                      <td>{e.actor_email ?? <span className="muted">system</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {q.data?.next_cursor ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCursor(q.data?.next_cursor ?? null)}
                  disabled={q.isFetching}
                >
                  Show older
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
